import { type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { NumberInput } from '../components/NumberInput.js';

const { t } = i18n;

export function PartnerNewPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [defaultSlaWorkdays, setDefaultSlaWorkdays] = useState(5);
  const [allowOperatorOrders, setAllowOperatorOrders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await api<{ id: string }>('/partners', {
        method: 'POST',
        body: {
          code,
          name,
          defaultSlaWorkdays,
          allowOperatorOrders,
          contactEmail: contactEmail || undefined,
          contactPhone: contactPhone || undefined,
        },
      });
      navigate(`/partners/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'partner_code_taken') {
        setError(t('partner.create.code_taken'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="max-w-md space-y-4">
      <h2 className="text-xl font-semibold">{t('partner.create.title')}</h2>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
        <Field label={t('partner.col.code')} hint="e.g. ALPHA">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            pattern="[A-Z0-9]{2,16}"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm font-mono"
          />
        </Field>
        <Field label={t('partner.col.name')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('partner.col.sla')}>
          <NumberInput
            integer
            min={0}
            max={365}
            value={defaultSlaWorkdays}
            onChange={(v) => setDefaultSlaWorkdays(v ?? 0)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm tabular-nums"
          />
        </Field>
        <Field label={t('customer.contact.email')}>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('customer.contact.phone')}>
          <input
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allowOperatorOrders}
            onChange={(e) => setAllowOperatorOrders(e.target.checked)}
          />
          {t('partner.create.allow_operator_orders')}
        </label>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={busy || !code || !name}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/partners')}
            className="rounded border border-border px-3 py-1.5 text-sm"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}
